import { useCallback, useEffect, useState } from 'react'
import { PrivacyType } from '@/utils/constants'
import { isTopicNotAuthorized, parseAjaxErrorMessage } from '@/utils/apiError'
import { createAnonymousApi } from '@/utils/anonymousApi'
import { topicToAlbumDetail } from '../mappers'
import { AlbumDetail } from '../types'

const anonymousApi = createAnonymousApi()

/**
 * Guest / public album fetch — no Cognito session required.
 * Works for public albums; private ones will fail (expected).
 *
 * `isPrivate` is a client-side courtesy, not the security boundary — the
 * backend's own visibility rules gate the `topicNoteList` response itself
 * (see `usePublicGallery`, which this mirrors). This just lets the guest
 * screen render a clear "this album is private" state instead of an empty
 * grid or a generic error.
 */
export function usePublicAlbum(shareId: string | undefined) {
  const [album, setAlbum] = useState<AlbumDetail | null>(null)
  const [isPrivate, setIsPrivate] = useState(false)
  const [loading, setLoading] = useState(Boolean(shareId))
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!shareId) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    setIsPrivate(false)
    try {
      const response = await anonymousApi.post('/ajax/topics', {
        requestType: 'topicNoteList',
        topicID: shareId,
        format: 'json',
        pageNo: 1,
      })
      if (response.data?.isError) {
        // The owner's `isAllowed()` rejection for a private topic — same
        // outcome as the visibility check below, just caught earlier
        // (the backend never sends topic data for it at all).
        if (isTopicNotAuthorized(response.data.errorMessage)) {
          setAlbum(null)
          setIsPrivate(true)
          return
        }
        throw new Error(parseAjaxErrorMessage(response.data.errorMessage, 'Album unavailable'))
      }
      const notes = response.data?.notes || []
      const topic = {
        ...response.data,
        notes: [...notes].reverse(),
        topicID: response.data.topicID || shareId,
      }
      if (String(topic.visibility) === String(PrivacyType.PRIVATE)) {
        setAlbum(topicToAlbumDetail(topic, false))
        setIsPrivate(true)
        return
      }
      setAlbum(topicToAlbumDetail(topic, false))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load shared album')
      setAlbum(null)
    } finally {
      setLoading(false)
    }
  }, [shareId])

  useEffect(() => {
    refresh()
  }, [refresh])

  return { album, isPrivate, loading, error, refresh }
}
